import { APP_LINKS, VENDOR_PLANS } from "../constants/site";
import { Seo } from "../components/site/Seo";
import { Reveal, SectionHead } from "../components/site/Shared";
import { Icon } from "../components/site/Icon";

const STEPS = [
  {
    icon: "KeyRound",
    title: "Register as a Service Pro",
    text: "Create your account with your name, email and password, and agree to the Vendor Terms.",
  },
  {
    icon: "BadgeCheck",
    title: "Complete verification",
    text: "Build your business profile with your services, service cities and KYC details (such as PAN/GSTIN).",
  },
  {
    icon: "Wrench",
    title: "Choose your plan",
    text: "Your profile becomes visible to customers once HomeAligns approves it and your subscription is active.",
  },
  {
    icon: "BellRing",
    title: "Receive opportunities",
    text: "See job requests from households in your city, manage bookings and your schedule, and get fast online settlements.",
  },
];

export default function Vendors() {
  return (
    <>
      <Seo
        title="For Service Professionals — Join HomeAligns"
        description="Register as a HomeAligns Service Pro: get verified, list your services and receive job requests near you. Starter ₹4,999 for 6 months, Pro ₹8,999 per year. No commission on bookings."
        path="/for-service-professionals"
      />
      <section className="bg-teal-deep py-20 text-white lg:py-28">
        <div className="texture-grid">
          <div className="container-site max-w-3xl">
            <Reveal>
              <p className="eyebrow mb-4 !text-teal-soft">For service professionals</p>
              <h1 className="font-display text-[clamp(34px,4.5vw,58px)] font-semibold leading-[1.06] tracking-tight">
                Your craft. Your business. Aligned.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
                Join HomeAligns as an independent service professional. Get verified, establish your
                presence, and access relevant service opportunities from households — with no
                commission deducted from your bookings.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a
                  href={APP_LINKS.registerVendor}
                  data-testid="vendors-hero-join-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-teal-deep shadow-xl transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Join as a Service Professional <Icon name="ArrowRight" className="h-4 w-4" />
                </a>
                <a href={APP_LINKS.login} data-testid="vendors-hero-login-btn" className="inline-flex items-center gap-2 rounded-full border border-white/35 px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-white/10">
                  Already registered? Log In
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-site py-20">
        <SectionHead
          eyebrow="How it works"
          title="From registration to your first booking."
        />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i + 1} className="card-site p-7">
              <span className="icon-tile">
                <Icon name={s.icon} className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-base font-bold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-site">
          <SectionHead
            eyebrow="Vendor plans"
            title="Platform access, honestly priced."
            sub="Subscriptions buy platform access and listed features only — they do not guarantee leads, bookings, revenue or availability. Existing subscribers keep their current terms."
          />
          <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
            {VENDOR_PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i + 1} className={`card-site p-8 ${p.highlight ? "ring-2 ring-teal" : ""}`}>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-teal">{p.name}</p>
                <p className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-semibold text-ink">{p.price}</span>
                  <span className="text-sm text-ink/55">{p.period}</span>
                </p>
                <p className="mt-3 text-sm text-ink/60">{p.blurb}</p>
                <a href={APP_LINKS.registerVendor} data-testid={`vendors-plan-${p.name.toLowerCase()}-btn`} className={`mt-7 w-full ${p.highlight ? "btn-primary" : "btn-secondary"}`}>
                  Choose {p.name}
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal className="mx-auto mt-10 max-w-3xl rounded-2xl border border-teal-deep/10 bg-mist/60 p-6">
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
              <Icon name="ShieldCheck" className="h-4 w-4 text-teal" /> Our side of the bargain
            </h3>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink/60">
              <li>· Providers are independent businesses — you run your work, your way.</li>
              <li>· Use customer information only for the specific request or job.</li>
              <li>· Plan prices and benefits are shown in the app before you pay. Payments currently run in Test Mode.</li>
            </ul>
          </Reveal>
        </div>
      </section>
    </>
  );
}
